import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Youtube } from "lucide-react";
import { CoverImage } from "@/components/CoverImage";
import { ytEmbed } from "@/lib/youtube";

const SPOTIFY_RE = /open\.spotify\.com\/(?:intl-[a-z-]+\/)?(album|track|playlist)\/([A-Za-z0-9]+)/i;

export const spotifyEmbed = (url) => {
    const match = (url || "").match(SPOTIFY_RE);
    if (!match) return null;
    return `https://open.spotify.com/embed/${match[1].toLowerCase()}/${match[2]}?theme=0`;
};

const youtubeId = (url) => {
    const match = (url || "").match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : null;
};

// Spotify first (it is the primary listen), YouTube only when there is no Spotify link.
export const playerFor = (release) => {
    const spotify = spotifyEmbed(release?.links?.spotify);
    if (spotify) return { kind: "spotify", src: spotify };
    const id = youtubeId(release?.links?.youtube);
    if (id) return { kind: "youtube", src: ytEmbed(id) };
    return null;
};

export const hasPlayer = (release) => playerFor(release) !== null;

export const PlayableCover = ({ release, open, onToggle, coverClassName = "" }) => {
    const player = playerFor(release);
    const compact = player?.kind === "spotify" && release.type === "Single";

    return (
        <div className="relative w-full h-full">
            <AnimatePresence mode="wait" initial={false}>
                {open && player ? (
                    <motion.div
                        key="player"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="absolute inset-0 bg-black flex items-center justify-center p-3"
                        data-testid={`release-player-${release.slug}`}
                    >
                        <iframe
                            title={`${release.title} player`}
                            src={player.src}
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            className={`w-full rounded-xl border-0 ${compact ? "h-[152px]" : "h-full"}`}
                            data-testid={`release-player-iframe-${release.slug}`}
                        />
                        <button
                            onClick={onToggle}
                            aria-label="Close player"
                            data-testid={`release-player-close-${release.slug}`}
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/80 border border-amber-500/40 text-amber-200 flex items-center justify-center hover:bg-amber-500 hover:text-black transition-all duration-200"
                        >
                            <X size={15} />
                        </button>
                    </motion.div>
                ) : (
                    <motion.div
                        key="cover"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="absolute inset-0"
                    >
                        <CoverImage release={release} className={`w-full h-full object-cover ${coverClassName}`} />
                        {player && (
                            <button
                                onClick={onToggle}
                                aria-label={`Play ${release.title}`}
                                data-testid={`release-play-button-${release.slug}`}
                                className="absolute inset-0 flex items-center justify-center group/play"
                            >
                                <span className="w-16 h-16 rounded-full bg-amber-500/90 text-black flex items-center justify-center shadow-[0_0_30px_rgba(200,151,43,0.45)] transition-transform duration-300 group-hover/play:scale-110">
                                    <Play size={24} fill="currentColor" className="ml-0.5" />
                                </span>
                            </button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export const WatchOnYouTube = ({ release }) => {
    const spotify = spotifyEmbed(release?.links?.spotify);
    const yt = release?.links?.youtube;
    if (!spotify || !yt) return null;
    return (
        <a
            href={yt}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`watch-on-youtube-${release.slug}`}
            className="mt-3 inline-flex items-center gap-1.5 font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-amber-300 transition-colors duration-200"
        >
            <Youtube size={12} /> Watch on YouTube
        </a>
    );
};
